<script setup lang="ts">
    import { gsap } from 'gsap';
    import { ref, onMounted, onBeforeUnmount } from 'vue';

    const {
        navigation: _navigation = {},
        hubs = [],
        enableHubSwitcher = false,
        isMenuOpen: initialIsMenuOpen = false
    } = defineProps<{
        navigation?: Record<string, unknown>;
        hubs?: AImageCardComponentStoryblok[];
        enableHubSwitcher?: boolean;
        isMenuOpen?: boolean;
    }>();
    const emit = defineEmits(['update:isMenuOpen']);

    const isMenuOpen = ref(initialIsMenuOpen);
    const { clampBody, releaseBody } = useFixedBody();

    const sideMenuController = useSideMenuModal();

    const menuAnimTl = gsap.timeline({ ease: 'linear', paused: true });

    onMounted(() => {
        menuAnimTl.to('.svg-menu--top', {
            y: 6,
            duration: 0.2
        });
        menuAnimTl.to(
            '.svg-menu--bottom',
            {
                y: -6,
                duration: 0.3
            },
            '<='
        );
        menuAnimTl.to(
            '.svg-menu--middle',
            {
                opacity: 0,
                duration: 0.3
            },
            '<='
        );

        menuAnimTl.to(
            '.svg-menu--top',
            {
                rotate: 45,
                duration: 0.3,
                transformOrigin: 'center'
            },
            '+=0.1'
        );
        menuAnimTl.to(
            '.svg-menu--bottom',
            {
                rotate: -45,
                duration: 0.3,
                transformOrigin: 'center'
            },
            '<='
        );
    });

    // using debounce because the watcher in LayoutHeader is executed twice on clicking the menuButton (blur doesn't work)
    const debounce = (callback: () => void, delay: number) => {
        let timer: number;
        return function () {
            clearTimeout(timer);
            timer = window.setTimeout(() => {
                callback();
            }, delay);
        };
    };

    const handleMenuFunc = () => {
        isMenuOpen.value = !isMenuOpen.value;
        emit('update:isMenuOpen', isMenuOpen.value);
        toggleButton();
    };

    const handleMenu = debounce(handleMenuFunc, 100);

    defineExpose({
        handleMenu
    });

    const toggleButton = () => {
        if (!isMenuOpen.value) {
            menuAnimTl.reverse();
            sideMenuController.close();
            releaseBody();
        } else {
            menuAnimTl.play();
            sideMenuController.open();
            clampBody();
        }
    };

    onBeforeUnmount(() => {
        if (isMenuOpen.value) {
            menuAnimTl.kill();
            sideMenuController.close();
            releaseBody();
        }
    });

    const isHubSwitcherOpen = ref(false);

    watch(isHubSwitcherOpen, () => {
        if (!isHubSwitcherOpen.value) {
            handleMenu();
        }
    });

    onUnmounted(() => {
        if (isMenuOpen.value) {
            releaseBody();
        }
    });
</script>
<template>
    <HeaderHubSwitcher
        v-if="isMenuOpen && enableHubSwitcher"
        v-model:is-hub-switcher-open="isHubSwitcherOpen"
        :hubs="hubs"
        :is-mobile="true"
    />

    <button
        class="mobile-menu-button"
        :aria-label="isMenuOpen ? 'Menü schließen' : 'Menü öffnen'"
        @click="handleMenu"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-menu"
        >
            <line
                class="svg-menu--top"
                x1="4"
                x2="20"
                y1="6"
                y2="6"
            />
            <line
                class="svg-menu--middle"
                x1="4"
                x2="20"
                y1="12"
                y2="12"
            />
            <line
                class="svg-menu--bottom"
                x1="4"
                x2="20"
                y1="18"
                y2="18"
            />
        </svg>
    </button>
</template>

<style scoped>
    @import '~/assets/scss/navigation/header-navigation-mobile.scss';
</style>
